// i18n key registry for diary-flow errors + UI strings (per W2 §6.3 +
// AGENTS.md §3 #14 繁體中文-only rule).

import { DIARY_MOOD_CODES } from "@/lib/validation/diary";
import { DIARY_RESTORE_DAYS } from "@/lib/retentionWindows";
import { MAX_UPLOAD_BYTES } from "@/lib/apiLimits";

export const DIARY_I18N = {
  // CRUD errors
  diary_not_found: "找不到這篇日記",
  diary_forbidden: "你無權限操作此日記",
  diary_private: "此日記為私人, 對方無法查看",
  /** W51 — the diary's linked source is gone (wish deleted, etc.). */
  diary_source_not_found: "找不到這篇日記的來源項目, 可能已被刪除",
  diary_create_failed: "日記建立失敗, 請稍後再試",
  diary_update_failed: "日記更新失敗, 請稍後再試",
  diary_delete_failed: "日記刪除失敗, 請稍後再試",
  diary_restore_failed: "日記還原失敗, 請稍後再試",
  // W40.12. This said 7. The diary window moved to 30 in W39.78 and the sentence
  // did not, so a person who could not get a diary back was told the window had
  // been 7 days when the product keeps them for 30 — the exact thing
  // `retentionWindows.ts` exists to stop, and the guard beside it missed it
  // because it checks the sentence *builders*, not the literals that sit next to
  // them. Derived now, and `retentionWindows.test.ts` reads this file.
  diary_restore_window_expired: `已超過 ${DIARY_RESTORE_DAYS} 天保留期, 無法還原`,
  diary_validation_failed: "日記欄位驗證失敗, 請檢查後重試",
  diary_mood_invalid: "心情代碼無效",

  // Laya-gated classify_mood
  classify_mood_unavailable: "心情建議功能暫時無法使用",
  classify_mood_failed: "分類失敗, 請稍後再試",
  classify_mood_using: "AI 正在讀取你的文字…",

  // Mood labels (繁體中文) — paired to DIARY_MOOD_CODES order.
  mood_label: (code: string): string => {
    const labels: Record<string, string> = {
      mood_excited_1: "興奮 (微弱)",
      mood_excited_2: "興奮 (輕度)",
      mood_excited_3: "興奮 (中度)",
      mood_excited_4: "興奮 (強烈)",
      mood_excited_5: "興奮 (非常強烈)",
      mood_happy_1: "開心 (微弱)",
      mood_happy_2: "開心 (輕度)",
      mood_happy_3: "開心 (中度)",
      mood_happy_4: "開心 (強烈)",
      mood_happy_5: "開心 (非常強烈)",
      mood_calm_1: "平靜 (微弱)",
      mood_calm_2: "平靜 (輕度)",
      mood_calm_3: "平靜 (中度)",
      mood_calm_4: "平靜 (強烈)",
      mood_calm_5: "平靜 (非常強烈)",
      mood_sad_1: "悲傷 (微弱)",
      mood_sad_2: "悲傷 (輕度)",
      mood_sad_3: "悲傷 (中度)",
      mood_sad_4: "悲傷 (強烈)",
      mood_sad_5: "悲傷 (非常強烈)",
      mood_angry_1: "煩躁 (微弱)",
      mood_angry_2: "煩躁 (輕度)",
      mood_angry_3: "煩躁 (中度)",
      mood_angry_4: "煩躁 (強烈)",
      mood_angry_5: "煩躁 (非常強烈)",
      mood_other_1: "其他 (微弱)",
      mood_other_2: "其他 (輕度)",
      mood_other_3: "其他 (中度)",
      mood_other_4: "其他 (強烈)",
      mood_other_5: "其他 (非常強烈)",
    };
    return labels[code] ?? code;
  },

  // Mood emoji shortcuts (5 per category — extract just the category+level
  // suffix and reuse via emoji map).  Emojis are data per AGENTS.md §3 #19,
  // not chrome (UI icon); see ARCHITECTURE exception.
  mood_emoji: (code: string): string => {
    const cat = code.split("_")[1] ?? "";
    const level = Number(code.split("_")[2] ?? 0);
    const base: Record<string, string[]> = {
      excited: ["🙂", "😀", "😆", "🤩", "🥳"],
      happy: ["🙂", "😊", "😄", "😁", "🤗"],
      calm: ["😶", "🙂", "😌", "😇", "🧘"],
      sad: ["😕", "😟", "😢", "😭", "💔"],
      angry: ["😑", "😠", "😡", "🤬", "🥵"],
      other: ["🤔", "😐", "😶", "🌫", "❓"],
    };
    if (!base[cat] || !level) return "❓";
    return base[cat][level - 1] ?? "❓";
  },

  // UI
  title_new_diary: "寫日記",
  title_edit_diary: "編輯日記",
  title_first_diary: "你的第一篇日記",
  privacy_label: "私人日記 (僅自己可見)",
  privacy_help: "私人日記對方看不到, 但你自己永遠能讀取",
  button_save: "儲存",
  button_cancel: "取消",
  button_discard: "放棄",
  button_delete: "刪除",
  button_restore: "還原",
  button_ai_suggest_mood: "AI 建議心情",
  button_add_photo: "新增圖片",
  button_remove_photo: "移除",
  placeholder_title: "標題 (可選)",
  placeholder_content: "今天想記錄什麼?",
  placeholder_first_diary_content: "跟我們在一起的這一天, 我想這樣記住…",
  heading_trash: "垃圾桶",
  // W39.88 — `heading_undo_window: "7 天內可還原"` removed. It was declared,
  // never referenced by any component, and **wrong**: the diary window is 30 days
  // (ADR-0087, W39.78). A dead key carrying a stale number is worse than no key,
  // because the next person to look for "where is 7 still written" finds it and
  // concludes the copy is fine. The live sentence is the `TrashList` subtitle,
  // built from `retentionDays`.
  // W39.69 — was 「8 MB」, which was the last copy in the product still saying
  // it. That drift was fixed by making every copy agree on **50 MB**.
  //
  // W50.1 — and then the number itself moved, for a reason no copy could see:
  // nginx rejects at 25 MB before the backend does, so the backend's 50 MB is
  // unreachable through the deployed product. Both constants now live in
  // `apiLimits.ts` and this sentence names the one a user can actually hit —
  // which is the same defect as before (two truths about one file), one level
  // up: every copy agreed, and they all agreed on a number nobody could reach.
  upload_too_large: `檔案超過 ${MAX_UPLOAD_BYTES / 1048576} MB 上限`,
  upload_wrong_type: "僅支援 JPEG / PNG / WebP / HEIC 圖片",
  upload_failed: "上傳失敗, 請重試",
} as const;

export type DiaryI18nKey = keyof Omit<typeof DIARY_I18N, "mood_label" | "mood_emoji">;

export function diaryErrorMessage(code: string | undefined, serverMessage?: string): string {
  if (serverMessage && serverMessage.trim()) return serverMessage;
  if (!code) return DIARY_I18N.diary_create_failed;
  switch (code) {
    case "DIARY_NOT_FOUND":
      return DIARY_I18N.diary_not_found;
    case "DIARY_FORBIDDEN":
      return DIARY_I18N.diary_forbidden;
    case "DIARY_PRIVATE":
      return DIARY_I18N.diary_private;
    case "DIARY_RESTORE_WINDOW_EXPIRED":
      return DIARY_I18N.diary_restore_window_expired;
    case "DIARY_MOOD_INVALID":
      return DIARY_I18N.diary_mood_invalid;
    case "DIARY_FEATURE_TEMPORARILY_UNAVAILABLE":
      return DIARY_I18N.classify_mood_unavailable;
    // W51. Added with the single-source-of-truth wishlist link (ADR-0159):
    // unlinking or re-linking a diary that points at a deleted wish raises this,
    // and without a case the user got the server's terse 「找不到來源項目」
    // instead of copy that says what happened.
    case "DIARY_SOURCE_NOT_FOUND":
      return DIARY_I18N.diary_source_not_found;
    case "VALIDATION_FAILED":
      return DIARY_I18N.diary_validation_failed;
    default:
      return DIARY_I18N.diary_create_failed;
  }
}

export const ALL_MOOD_CODES: readonly string[] = DIARY_MOOD_CODES;
